import { countsOf, selectionCoverage } from '@/components/ui/SelectionLabel'

// THE lean bar: one bar split left / center / right by article counts, the
// one element every page uses for a lean mix (Sam, 2026-11-01: there were
// seven copies, with different corners, tracks and screen-reader text). The
// identity mocks draw it in three heights, always fully rounded:
//   row    6px   list rows (homepage, chapters, topics, story foot)
//   list  10px   a /storylines row; a phone's coverage block
//   block 12px   a page's coverage block (story, storyline, topic)
//
// Give it `counts` ({ left, center, right }; percentages work too) or a story
// `node` (its counts are read the way the label reads them). A node with no
// counts (old edition rows) falls back to its label's colour so it still
// wears a mark.
//
// Pages don't place it directly: a list row uses LeanMark (label over the
// bar) and a page's own coverage uses CoverageBlock (counts over the bar).
//
// Screen readers hear the counts ("Coverage: 38 left, 125 center, 59
// right") unless `decorative` is set, for bars whose counts or share are
// printed right beside them: one fact, announced once.

export const LEAN_SIZES = { row: 'h-1.5', list: 'h-2.5', block: 'h-3' }

const BAR = {
  left: 'bg-blue-600',
  center: 'bg-zinc-500',
  right: 'bg-red-600',
}

const FALLBACK = {
  left: 'bg-blue-600',
  leans_left: 'bg-blue-600',
  left_center: 'bg-blue-600',
  right: 'bg-red-600',
  leans_right: 'bg-red-600',
  center_right: 'bg-red-600',
}

export default function LeanBar({
  node,
  counts,
  size = 'row',
  decorative = false,
  className = '',
}) {
  const height = LEAN_SIZES[size] || LEAN_SIZES.row
  const a11y = (label) =>
    decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label }

  const [left, center, right] = counts
    ? [counts.left || 0, counts.center || 0, counts.right || 0]
    : countsOf(node)
  const total = left + center + right
  if (total > 0) {
    const width = (n) => `${((n / total) * 100).toFixed(1)}%`
    return (
      <span
        className={`flex ${height} overflow-hidden rounded-full ${className}`}
        {...a11y(`Coverage: ${left} left, ${center} center, ${right} right`)}
      >
        <span className={BAR.left} style={{ width: width(left) }} />
        <span className={BAR.center} style={{ width: width(center) }} />
        <span className={BAR.right} style={{ width: width(right) }} />
      </span>
    )
  }

  const sel = node && selectionCoverage(node)
  if (!sel) return null
  return (
    <span
      className={`block ${height} rounded-full ${FALLBACK[sel.key] || BAR.center} ${className}`}
      {...a11y(sel.text ? `Coverage: ${sel.text}` : 'Coverage')}
    />
  )
}
